import { useState } from "react";
import { useSearchParams } from "react-router";
import { MRT_TableState, MRT_RowData } from "material-react-table";

interface UseTableStateOptions<TData extends MRT_RowData> {
  initialState?: Partial<MRT_TableState<TData>>;
}

export const useTableState = <TData extends MRT_RowData>(
  options: UseTableStateOptions<TData> = {}
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tableState, setTableState] = useState<Partial<MRT_TableState<TData>>>(() => {
    const params = Object.fromEntries(searchParams.entries());
    return {
      columnFilters: params.filters ? JSON.parse(params.filters) : [],
      globalFilter: params.search || '',
      sorting: params.sort ? JSON.parse(params.sort) : [],
      showGlobalFilter: true,
      density: 'compact',
      pagination: { pageSize: 10, pageIndex: 0 },
      columnPinning: { left: ['mrt-row-select', 'id'], right: ['actions'] },
      ...options.initialState,
    };
  });

  const handleTableStateChange = (
    updater: (state: Partial<MRT_TableState<TData>>) => Partial<MRT_TableState<TData>>
  ) => {
    setTableState((prev) => {
      const newState = updater(prev);
      const newSearchParams = new URLSearchParams(searchParams);

      if (newState.columnFilters?.length) {
        newSearchParams.set('filters', JSON.stringify(newState.columnFilters));
      } else {
        newSearchParams.delete('filters');
      }

      if (newState.globalFilter) {
        newSearchParams.set('search', newState.globalFilter);
      } else {
        newSearchParams.delete('search');
      }

      if (newState.sorting?.length) {
        newSearchParams.set('sort', JSON.stringify(newState.sorting));
      } else {
        newSearchParams.delete('sort');
      }

      setSearchParams(newSearchParams);
      return newState;
    });
  };

  return {
    tableState,
    handleTableStateChange,
    onColumnFiltersChange: (updater: any) => {
      const filters = typeof updater === 'function' ? updater(tableState.columnFilters ?? []) : updater;
      handleTableStateChange((prev) => ({ ...prev, columnFilters: filters }));
    },
    onGlobalFilterChange: (filter: string) => 
      handleTableStateChange((prev) => ({ ...prev, globalFilter: filter })),
    onSortingChange: (updater: any) => {
      const sorting = typeof updater === 'function' ? updater(tableState.sorting ?? []) : updater;
      handleTableStateChange((prev) => ({ ...prev, sorting }));
    },
  };
}; 